import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { isSiteAdminUser } from '../config/admin';
import { getAuthUserId } from '../utils/authUser';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';

interface SiteAdminUserRecord {
  id: string;
  userID: string;
  userName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  displayName: string;
  status?: string | null;
  isSiteAdmin?: boolean;
  bootstrapSiteAdmin?: boolean;
  dbSiteAdmin?: boolean;
  effectiveSiteAdmin?: boolean;
  siteAdminGrantedAt?: string | null;
  siteAdminGrantedBy?: string | null;
  siteAdminRevokedAt?: string | null;
  siteAdminRevokedBy?: string | null;
}

const ADMIN_FILTER_OPTIONS = [
  { value: 'all', label: 'All users' },
  { value: 'effective-admins', label: 'Effective admins' },
  { value: 'db-admins', label: 'DB-backed admins' },
  { value: 'non-admins', label: 'Non-admins' },
] as const;

type AdminFilterValue = typeof ADMIN_FILTER_OPTIONS[number]['value'];

const formatTimestamp = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatActor = (value?: string | null) => value || '—';

const buildFullName = (entry: SiteAdminUserRecord) => [entry.firstName, entry.lastName].filter(Boolean).join(' ').trim();

const SiteAdminUsersPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const isAllowed = isSiteAdminUser(user as any);
  const [users, setUsers] = useState<SiteAdminUserRecord[]>([]);
  const [search, setSearch] = useState('');
  const [adminFilter, setAdminFilter] = useState<AdminFilterValue>('all');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<SiteAdminUserRecord | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(buildEventsApiUrl('/api/site-admin/users'), {
        headers: buildAuthHeaders(user),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `Failed to load users (${response.status})`);
      }
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load site admin users');
    }
  }, [user]);

  useEffect(() => {
    if (isAllowed) {
      loadUsers();
    }
  }, [isAllowed, loadUsers]);

  const filteredUsers = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return users.filter((entry) => {
      const matchesSearch = !normalized || JSON.stringify([
        entry.id,
        entry.userID,
        entry.userName,
        entry.displayName,
        entry.email,
        entry.firstName,
        entry.lastName,
        buildFullName(entry),
      ]).toLowerCase().includes(normalized);

      if (!matchesSearch) return false;

      if (adminFilter === 'effective-admins') return Boolean(entry.effectiveSiteAdmin);
      if (adminFilter === 'db-admins') return Boolean(entry.dbSiteAdmin);
      if (adminFilter === 'non-admins') return !entry.effectiveSiteAdmin;
      return true;
    });
  }, [adminFilter, search, users]);

  const handleSiteAdminToggle = async (entry: SiteAdminUserRecord, nextValue: boolean) => {
    try {
      setPendingUserId(entry.id);
      setError(null);
      setSuccess(null);
      const response = await fetch(buildEventsApiUrl(`/api/site-admin/users/${encodeURIComponent(entry.id)}/site-admin`), {
        method: 'PATCH',
        headers: buildJsonAuthHeaders(user),
        body: JSON.stringify({ isSiteAdmin: nextValue }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `Failed to update site admin status (${response.status})`);
      }
      setUsers((current) => current.map((candidate) => candidate.id === entry.id ? data.user : candidate));
      setSuccess(`${entry.displayName || entry.id} ${nextValue ? 'is now' : 'is no longer'} a DB-backed site admin.`);
      if (!nextValue) {
        setDemoteTarget(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to update site admin status');
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Site Admin Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Promote or demote DB-backed site admins while preserving bootstrap fallback access.
          </Typography>

          {!isAllowed && (
            <Alert severity="warning">
              You are signed in as <strong>{userId || 'unknown'}</strong>. This area is intended for site admins.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small"
              label="Search users"
              placeholder="Name, username, email, id..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
              disabled={!isAllowed}
            />
            <TextField
              select
              size="small"
              label="Filter"
              value={adminFilter}
              onChange={(event) => setAdminFilter(event.target.value as AdminFilterValue)}
              sx={{ minWidth: 220 }}
              disabled={!isAllowed}
            >
              {ADMIN_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={loadUsers} disabled={!isAllowed || Boolean(pendingUserId)}>
              Refresh
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="h6">Safety notes</Typography>
              <Typography variant="body2" color="text.secondary">
                Bootstrap site admins keep access even if their DB flag is turned off. The backend also refuses to remove the last remaining effective site admin.
              </Typography>
            </Stack>
          </Paper>

          <Typography variant="body2" color="text.secondary">
            Showing {filteredUsers.length} of {users.length} users.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email / ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Site Admin Access</TableCell>
                  <TableCell>Audit</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((entry) => {
                  const isPending = pendingUserId === entry.id;
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.displayName || entry.userName || entry.id}</Typography>
                          {buildFullName(entry) && (
                            <Typography variant="caption" color="text.secondary">{buildFullName(entry)}</Typography>
                          )}
                          {entry.userName && <Typography variant="caption" color="text.secondary">@{entry.userName}</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2">{entry.email || '—'}</Typography>
                          {entry.userID && entry.userID !== entry.id && (
                            <Typography variant="caption" color="text.secondary">User ID: {entry.userID}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">Doc ID: {entry.id}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={entry.status || 'active'} size="small" color={entry.status === 'deleted' ? 'default' : 'success'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {entry.effectiveSiteAdmin ? (
                            <Chip label="Effective admin" size="small" color="primary" />
                          ) : (
                            <Chip label="Not admin" size="small" variant="outlined" />
                          )}
                          {entry.bootstrapSiteAdmin && <Chip label="Bootstrap" size="small" color="secondary" variant="outlined" />}
                          {entry.dbSiteAdmin && <Chip label="DB flag" size="small" color="success" variant="outlined" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="caption" color="text.secondary">
                            Granted: {formatTimestamp(entry.siteAdminGrantedAt)} by {formatActor(entry.siteAdminGrantedBy)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Revoked: {formatTimestamp(entry.siteAdminRevokedAt)} by {formatActor(entry.siteAdminRevokedBy)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant={entry.dbSiteAdmin ? 'outlined' : 'contained'}
                          color={entry.dbSiteAdmin ? 'warning' : 'primary'}
                          size="small"
                          disabled={!isAllowed || isPending || entry.status === 'deleted'}
                          onClick={() => {
                            if (entry.dbSiteAdmin) {
                              setDemoteTarget(entry);
                              return;
                            }
                            handleSiteAdminToggle(entry, true);
                          }}
                        >
                          {isPending ? 'Saving...' : entry.dbSiteAdmin ? 'Demote' : 'Promote'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">No users matched this filter.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <Dialog open={Boolean(demoteTarget)} onClose={() => !pendingUserId && setDemoteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm site admin demotion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {demoteTarget
              ? `Demote ${demoteTarget.displayName || demoteTarget.id} from DB-backed site admin access? Bootstrap admins keep fallback access, and the backend will block any change that would remove the last effective site admin.`
              : 'Are you sure you want to demote this user?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemoteTarget(null)} disabled={Boolean(pendingUserId)}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!demoteTarget || Boolean(pendingUserId)}
            onClick={() => demoteTarget && handleSiteAdminToggle(demoteTarget, false)}
          >
            {pendingUserId && demoteTarget?.id === pendingUserId ? 'Demoting...' : 'Confirm demotion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SiteAdminUsersPage;
